import {
  Box,
  Collapse,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  ExpandMore,
  ImageOutlined,
  RadioButtonChecked,
  ShortText,
  TextFields,
} from "@mui/icons-material";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useState } from "react";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
} from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import ComponentEditor from "./ComponentEditor";
import {
  BRANCH_FIELD_TYPES,
  COMPONENT_TYPES,
  TYPE_GROUPS,
  componentSummary,
} from "./builderConstants";

const labelSx = {
  ...typography.bodySmall,
  fontWeight: 500,
  color: ColorsConstants.primaryBackgroundColor,
  mb: 0.5,
};

const hintSx = { ...typography.bodySmall, color: grayFontColor, mt: 0.5 };

// Icon shown on each category header of the "Add content" menu.
const GROUP_ICONS = {
  Text: TextFields,
  Media: ImageOutlined,
  "Text answer": ShortText,
  Selection: RadioButtonChecked,
};

/**
 * "Next screen depends on answer" for a screen's footer. `laterScreens` are the
 * screens after this one ({ id, title, index }); a screen needs its id (given on
 * the first save) before it can be picked.
 */
const BranchSettings = ({ screen, laterScreens, onChange }) => {
  const branch = screen.footer?.branch;
  const choices = (screen.components || []).filter((c) => BRANCH_FIELD_TYPES.includes(c.type));
  const field = choices.find((c) => c.id === branch?.component_id);
  const setBranch = (next) => onChange({ footer: { ...screen.footer, branch: next } });

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={typography.bodySmall}>Next screen depends on answer</Typography>
        <Switch
          size="small"
          checked={Boolean(branch)}
          disabled={!branch && (choices.length === 0 || laterScreens.length === 0)}
          onChange={(e) =>
            setBranch(e.target.checked ? { component_id: choices[0].id, targets: {} } : undefined)
          }
          inputProps={{ "aria-label": "Next screen depends on answer" }}
        />
      </Stack>
      {!branch && (
        <Typography sx={hintSx}>
          Go to a different screen for each answer of a single choice or dropdown on this
          screen. Add the question and the screens after this one first.
        </Typography>
      )}
      {branch && (
        <>
          <Typography sx={{ ...labelSx, mt: 1 }}>Depends on</Typography>
          <TextField
            size="small"
            fullWidth
            select
            value={field ? field.id : ""}
            InputProps={{ sx: typography.bodyLarge }}
            onChange={(e) => setBranch({ component_id: e.target.value, targets: {} })}
          >
            {choices.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={typography.bodyLarge}>
                {c.label || c.key || "Untitled"}
              </MenuItem>
            ))}
          </TextField>
          {(field?.options || []).map((option) => (
            <Stack key={option.id} direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
              <Typography noWrap sx={{ ...typography.bodySmall, flex: 1, minWidth: 0 }}>
                {option.title}
              </Typography>
              <TextField
                size="small"
                select
                value={branch.targets?.[option.id] || ""}
                SelectProps={{ displayEmpty: true }}
                InputProps={{ sx: typography.bodyLarge }}
                inputProps={{ "aria-label": `Next screen for ${option.title}` }}
                onChange={(e) =>
                  setBranch({ ...branch, targets: { ...branch.targets, [option.id]: e.target.value } })
                }
                sx={{ flex: 1.5 }}
              >
                <MenuItem value="" disabled sx={typography.bodyLarge}>
                  Pick a screen
                </MenuItem>
                {laterScreens.map((s) => (
                  <MenuItem key={s.index} value={s.id || ""} disabled={!s.id} sx={typography.bodyLarge}>
                    {`Screen ${s.index + 1}: ${s.title}${s.id ? "" : " (saving...)"}`}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          ))}
          <Typography sx={hintSx}>
            Every screen after this one must be picked for an answer, and each of them ends
            the form.
          </Typography>
        </>
      )}
    </Box>
  );
};

const Chevron = ({ open }) => (
  <ExpandMore
    sx={{
      color: grayFontColor,
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform .15s",
    }}
  />
);

const DragHandle = ({ handleProps, label }) => (
  <IconButton
    size="small"
    aria-label={label}
    {...handleProps}
    onClick={(e) => e.stopPropagation()}
    sx={{ cursor: "grab", touchAction: "none", color: grayFontColor }}
  >
    <DragIndicatorIcon fontSize="small" />
  </IconButton>
);

const ItemRow = ({
  id,
  open,
  onToggle,
  chip,
  summary,
  onDelete,
  onDuplicate,
  duplicateDisabled,
  children,
  footer,
  outerRef,
  outerSx,
  handleProps,
}) => (
  <Box
    id={id}
    ref={outerRef}
    sx={{
      border: `1px solid ${open ? "#C7CDDB" : borderColors.cardBorderPrimaryGrayColor}`,
      borderRadius: "6px",
      mt: 1,
      backgroundColor: open ? "#FFFFFF" : "#F8F9FB",
      ...outerSx,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={onToggle}
      sx={{ px: 1.25, py: 1, cursor: "pointer" }}
    >
      {handleProps && <DragHandle handleProps={handleProps} label="Drag to reorder" />}
      <Box
        component="span"
        sx={{
          ...typography.bodySmall,
          px: 1,
          borderRadius: "10px",
          whiteSpace: "nowrap",
          backgroundColor: footer ? "#E6F6EC" : dashboardCardBgColor,
          color: footer ? "#1FA45A" : ColorsConstants.primaryBackgroundColor,
        }}
      >
        {chip}
      </Box>
      <Typography noWrap sx={{ ...typography.bodySmall, flex: 1, minWidth: 0 }}>
        {summary}
      </Typography>
      {onDuplicate && (
        <Tooltip title={duplicateDisabled ? "This screen is at its component limit" : "Duplicate"}>
          <span>
            <IconButton
              size="small"
              aria-label="Duplicate"
              disabled={duplicateDisabled}
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {onDelete && (
        <IconButton
          size="small"
          aria-label="Delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
      <Chevron open={open} />
    </Stack>
    <Collapse in={open} unmountOnExit>
      <Box sx={{ px: 1.25, pb: 1.5 }}>{children}</Box>
    </Collapse>
  </Box>
);

const SortableItemRow = ({ sortableId, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortableId });
  return (
    <ItemRow
      {...props}
      outerRef={setNodeRef}
      outerSx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: "relative",
        zIndex: isDragging ? 2 : "auto",
      }}
      handleProps={{ ...attributes, ...listeners }}
    />
  );
};

/**
 * One screen of the form: title, its components, the footer button and "Add content".
 */
const ScreenCard = ({
  screen,
  sortableId,
  index,
  total,
  open,
  openItem,
  limits,
  formId,
  lockedKeys,
  keyOwners,
  ruleFields,
  laterScreens,
  isBranchTarget,
  onToggle,
  onToggleItem,
  onScreenChange,
  onComponentChange,
  onAddComponent,
  onDeleteComponent,
  onDuplicateComponent,
  onDeleteScreen,
  onReorderComponents,
  uploadImage,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const sortable = useSortable({ id: sortableId });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const components = screen.components || [];
  const atLimit = components.length >= (limits?.max_components_per_screen || 50);
  const isLast = index === total - 1;
  const footerLabel = screen.footer?.label || "";
  const footerMax = limits?.footer_label_max;
  const footerTooLong = Boolean(footerMax) && footerLabel.length > footerMax;

  return (
    <Box
      id={`screen-${index}`}
      ref={sortable.setNodeRef}
      sx={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.6 : 1,
        position: "relative",
        zIndex: sortable.isDragging ? 3 : "auto",
        border: `1px solid ${open ? ColorsConstants.primaryBackgroundColor : borderColors.cardBorderPrimaryGrayColor}`,
        borderRadius: "8px",
        mb: 1.25,
        backgroundColor: "#FFFFFF",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        onClick={onToggle}
        sx={{ px: 1.75, py: 1.25, cursor: "pointer" }}
      >
        {total > 1 && (
          <DragHandle
            handleProps={{ ...sortable.attributes, ...sortable.listeners }}
            label="Drag to reorder screen"
          />
        )}
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor, whiteSpace: "nowrap" }}>
          Screen {index + 1}
        </Typography>
        <Typography noWrap sx={{ ...typography.bodyLarge, fontWeight: 500, flex: 1, minWidth: 0 }}>
          {screen.title}
        </Typography>
        {total > 1 && (
          <IconButton
            size="small"
            aria-label="Delete screen"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteScreen();
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
        <Chevron open={open} />
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            px: 1.75,
            pb: 1.75,
            pt: 1.5,
            borderTop: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          }}
        >
          <Typography sx={labelSx}>Screen title</Typography>
          <TextField
            size="small"
            fullWidth
            value={screen.title}
            InputProps={{ sx: typography.bodyLarge }}
            onChange={(e) => onScreenChange({ title: e.target.value })}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) =>
              over && active.id !== over.id && onReorderComponents(active.id, over.id)
            }
          >
            <SortableContext
              items={components.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
          {components.map((component, ci) => {
            const itemId = `${index}-${component.id}`;
            return (
              <SortableItemRow
                key={component.id}
                sortableId={component.id}
                id={`component-${component.id}`}
                open={openItem === itemId}
                onToggle={() => onToggleItem(itemId)}
                chip={COMPONENT_TYPES[component.type]?.label || component.type}
                summary={`${componentSummary(component)}${
                  component.visible_when?.length ? " · conditional" : ""
                }`}
                onDelete={() => onDeleteComponent(ci)}
                onDuplicate={() => onDuplicateComponent(ci)}
                duplicateDisabled={atLimit}
              >
                <ComponentEditor
                  component={component}
                  formId={formId}
                  limits={limits}
                  lockedKey={lockedKeys?.[component.id]}
                  keyOwners={keyOwners}
                  screenIndex={index}
                  ruleFields={ruleFields}
                  uploadImage={uploadImage}
                  onChange={(patch) => onComponentChange(ci, patch)}
                />
              </SortableItemRow>
            );
          })}

            </SortableContext>
          </DndContext>

          <ItemRow
            footer
            open={openItem === `${index}-footer`}
            onToggle={() => onToggleItem(`${index}-footer`)}
            chip="Footer button"
            summary={screen.footer?.label}
          >
            <Typography sx={{ ...labelSx, mt: 1 }}>Button label</Typography>
            <TextField
              size="small"
              fullWidth
              value={screen.footer?.label || ""}
              InputProps={{ sx: typography.bodyLarge }}
              error={footerTooLong}
              helperText={
                footerTooLong
                  ? `${footerLabel.length}/${footerMax} · Too long: at most ${footerMax} characters`
                  : isLast || isBranchTarget
                    ? "Completes the form and sends answers"
                    : screen.footer?.branch
                      ? "Goes to the screen picked for the answer"
                      : `Goes to screen ${index + 2}`
              }
              onChange={(e) => onScreenChange({ footer: { ...screen.footer, label: e.target.value } })}
            />
            {!isBranchTarget && (
              <BranchSettings
                screen={screen}
                laterScreens={laterScreens || []}
                onChange={onScreenChange}
              />
            )}
          </ItemRow>

          <Box sx={{ mt: 1.25 }}>
            <Box
              component="button"
              type="button"
              disabled={atLimit}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                ...typography.bodySmall,
                width: "100%",
                textAlign: "left",
                px: 1.5,
                py: 1,
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
                border: `1px dashed ${ColorsConstants.primaryBackgroundColor}`,
                color: ColorsConstants.primaryBackgroundColor,
                cursor: "pointer",
                "&:disabled": { opacity: 0.5, cursor: "default" },
              }}
            >
              + Add content
            </Box>
            {atLimit && (
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
                A screen can have up to {limits?.max_components_per_screen || 50} components.
              </Typography>
            )}
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              MenuListProps={{ dense: true, sx: { py: 0 } }}
              PaperProps={{ sx: { minWidth: 220, borderRadius: "8px" } }}
            >
              {TYPE_GROUPS.flatMap((group, index) => {
                const GroupIcon = GROUP_ICONS[group];
                return [
                  index > 0 && <Divider key={`${group}-divider`} sx={{ my: "0 !important" }} />,
                  <ListSubheader
                    key={group}
                    sx={{
                      ...typography.bodySmall,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      lineHeight: "32px",
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: ColorsConstants.primaryBackgroundColor,
                      backgroundColor: dashboardCardBgColor,
                    }}
                  >
                    {GroupIcon && <GroupIcon sx={{ fontSize: 16 }} />}
                    {group}
                  </ListSubheader>,
                  ...Object.entries(COMPONENT_TYPES)
                    .filter(([, meta]) => meta.group === group)
                    .map(([type, meta]) => (
                      <MenuItem
                        key={type}
                        sx={{ ...typography.bodyLarge, pl: 4.5 }}
                        onClick={() => {
                          setMenuAnchor(null);
                          onAddComponent(type);
                        }}
                      >
                        {meta.label}
                      </MenuItem>
                    )),
                ].filter(Boolean);
              })}
            </Menu>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ScreenCard;
